// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { withLeaflet } from 'react-leaflet';
import { get } from 'lodash';
import DragEvent from 'leaflet';
import ApiForm from 'esm-web-pro/containers/forms/ApiForm';
import type { LeafletContext } from 'react-leaflet/lib/types';
import typeof History from 'react-router-dom';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import BaseEditor, { DRAW_FG } from 'esm-web-pro/containers/map/editor/BaseEditor';
import {
    COORDINATES_EDIT_ALLOWED_DIGITS,
    LAT_LNG_MAX_MARGIN,
    reverseTransformPointCoords,
    transformPointCoords,
} from 'esm-web-pro/containers/pages/network/Util';
import { getCoordinateSystem } from 'esm-web-pro/redux/networks';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { ApiClient } from 'speed-js-core';
import type { UUID } from 'esm-web-pro/types/common';


type Props = {
    id: string,
    point: Object,
    onClose: Function,
    onSave: Function,
    resource: string,
    match: Object,
    history: Object,
    editor: BaseEditor,
    leaflet: LeafletContext,
    history: History,
    setSensitivity: (key: string, sensitivity: boolean) => void,
    coordinateSystem: string,
    getConnectedLines: Function,
}

let connectedLines: Array<Object> = [];

let objectLayerGroup;

const getNewConnectedLinesWhenLoading = (layer: Object) => {
    if (layer.feature) {
        const index = connectedLines.findIndex((line) => line.feature.id === layer.feature.id);

        if (index !== -1) {
            layer.setLatLngs(connectedLines[index].getLatLngs());
            layer.pointMovedIndex = connectedLines[index].pointMovedIndex;
            connectedLines[index] = layer;
        }
    }
};

const GisPointEditor = (props: Props) => {
    const formRef = React.useRef(null);
    const layerRef = React.useRef(null);
    const resetLayer = React.useRef(true);
    const {
        point, onClose, onSave, editor, leaflet: { map }, match, history, setSensitivity, id,
        resource, coordinateSystem, getConnectedLines,
    } = props;

    const moveLinesWithPoint = (lat, lng) => {
        if (!layerRef.current) return;

        connectedLines.forEach((line) => {
            const newCoords = line.getLatLngs();
            newCoords[line.pointMovedIndex] = {
                lat: Number(lat.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
                lng: Number(lng.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
            };
            line.setLatLngs(newCoords);
            if (line.contentTooltip) {
                line.contentTooltip.setLatLng(line.getCenter());
            }
        });
    };

    const handleDrag = (e: DragEvent) => {
        moveLinesWithPoint(e.latlng.lat, e.latlng.lng);
        let coords = [Number(e.latlng.lng.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
            Number(e.latlng.lat.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS))];

        if (coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM) {
            coords = transformPointCoords(coords);
        }

        if (formRef.current) {
            formRef.current.getFormikBag().setFieldValue('coordinates', {
                ...point.coordinates,
                coordinates: coords,
            });
        }
    };

    React.useEffect(() => {
        map.eachLayer((l) => {
            if (l.feature && l.feature.properties.identity === point.identity) {
                l.pm.enable({
                    allowSelfIntersection: false,
                });
                l.on('drag', handleDrag);
                layerRef.current = l;

                const key = `${point.coordinates.coordinates[0]}-${point.coordinates.coordinates[1]}`;

                connectedLines = get(map, ['coordsToLayers', key, 'lines']) || [];
                getConnectedLines(point.uuid).then((res) => {
                    connectedLines = connectedLines.filter((cLine) => res.includes(cLine.feature.id));
                    connectedLines.forEach((line) => {
                        const startCoords = line.getLatLngs()[0];
                        const endCoords = line.getLatLngs()[line.getLatLngs().length - 1];
                        if (startCoords.equals(l.getLatLng(), LAT_LNG_MAX_MARGIN)) {
                            line.pointMovedIndex = 0;
                        } else if (endCoords.equals(l.getLatLng(), LAT_LNG_MAX_MARGIN)) {
                            line.pointMovedIndex = line.getLatLngs().length - 1;
                        }
                    });
                });
            }
        });
        editor.registerLoadingCallback(getNewConnectedLinesWhenLoading);

        // Focus the point with its connected lines so that their position do not reset
        editor.focus([layerRef.current, ...connectedLines]);

        // Add the edited point to drawing feature group so that it is always visible
        // because there are zoom restrictions for its feature group
        objectLayerGroup = layerRef.current.pm._layerGroup;
        objectLayerGroup.removeLayer(layerRef.current);
        layerRef.current.addTo(editor.featureGroups[DRAW_FG].current.leafletElement);
        return () => {
            editor.focus();
            editor.featureGroups[DRAW_FG].current.leafletElement.removeLayer(layerRef.current);
            layerRef.current.addTo(objectLayerGroup);
        };
    }, [layerRef.current]);

    const returnToOriginal = () => {
        connectedLines.forEach((connectedLine) => {
            connectedLine.setLatLngs(connectedLine.feature.geometry.coordinates.map((c) => [c[1], c[0]]));
        });
    };

    React.useEffect(() => {
        if (layerRef.current && formRef.current) {
            const coords = layerRef.current.getLatLng();

            const newCoords = coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM
                ? transformPointCoords([coords.lng, coords.lat]) : [coords.lng, coords.lat];

            formRef.current.getFormikBag().setFieldValue('coordinates', {
                ...point.coordinates,
                coordinates: newCoords,
            });

            layerRef.current.on('drag', handleDrag);
        }
    }, [coordinateSystem, layerRef, formRef, point]);

    const handleClose = () => {
        if (resetLayer.current) {
            if (layerRef.current) {
                layerRef.current.pm.disable();
                layerRef.current.setLatLng([point.coordinates.coordinates[1], point.coordinates.coordinates[0]]);
            }
            returnToOriginal();
        }
        // editor._loadFeatures();
        onClose();
    };

    React.useEffect(() => () => {
        editor.unregisterLoadingCallback(getNewConnectedLinesWhenLoading);
        handleClose();
        connectedLines = [];
    }, []);

    const handleCancel = () => {
        onClose();
    };

    const handleChange = async () => {
        if (formRef && formRef.current) {
            await formRef.current.getFormikBag().validateForm();
            let { values: { coordinates } } = formRef.current.getFormikBag();

            if (coordinates.coordinates.some((v) => !v)) {
                return;
            }

            if (coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM) {
                coordinates = {
                    ...coordinates,
                    coordinates: reverseTransformPointCoords(coordinates.coordinates),
                };
            }

            moveLinesWithPoint(coordinates.coordinates[1], coordinates.coordinates[0]);

            const newCoords = {
                lat: Number(coordinates.coordinates[1].toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
                lng: Number(coordinates.coordinates[0].toFixed(COORDINATES_EDIT_ALLOWED_DIGITS)),
            };

            if (layerRef) {
                layerRef.current.setLatLng(newCoords);
            }
        }
    };

    const handleUpdate = (response: Object) => {
        // Update the uuid of the objects if it changed (that could happen when an active object is edited
        // and a draft one is created)
        onSave({ ...response.properties, coordinates: response.geometry, uuid: response.id });
        handleClose();
    };

    const handleSubmit = (values) => {
        if (!layerRef.current) {
            throw Error('There is no corresponding layer on the map for this line.');
        }

        layerRef.current.setLatLng(layerRef.current.toGeoJSON().geometry.coordinates
            .map((c) => Number(c.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS))).reverse());

        const { point: point11, ...rest } = values;
        return {
            ...rest,
            coordinates: {
                ...values.coordinates,
                coordinates: layerRef.current.toGeoJSON().geometry.coordinates
                    .map((c) => Number(c.toFixed(COORDINATES_EDIT_ALLOWED_DIGITS))),
            },
        };
    };

    const handleSave = (response) => {
        resetLayer.current = false;
        connectedLines.forEach((line) => {
            const newCoords = line.getLatLngs();
            newCoords[line.pointMovedIndex] = response.geometry.coordinates.slice().reverse();
            line.setLatLngs(newCoords);
        });
        if (layerRef.current) {
            layerRef.current.pm.disable();
        }
        handleUpdate(response);
    };

    const getInitial = () => {
        const initial = {
            ...point,
        };

        if (layerRef.current) {
            initial.coordinates = {
                ...initial.coordinates,
                coordinates: coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM
                    ? transformPointCoords(point.coordinates.coordinates) : point.coordinates.coordinates,
            };
        }
        return initial;
    };

    return (
        <>
            <ApiForm
                objectType={point.type}
                forwardRef={formRef}
                resourceName={resource}
                objectId={point.uuid}
                excludeFields={[]}
                initial={getInitial()}
                onSave={handleSave}
                onClose={handleCancel}
                onSubmit={handleSubmit}
                onChange={handleChange}
                height="100%"
                partialUpdate
            />
        </>
    );
};

const apiCallback = (apiClient: ApiClient, props: Props) => ({
    getConnectedLines: {
        send: (id: UUID) => apiClient.resources.gisPoint.connectedLines(id),
    },
});

export default connectAPI(apiCallback)(
    connect((state) => ({ coordinateSystem: getCoordinateSystem(state) }),
        (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))(
        withRouter(
            withLeaflet(GisPointEditor),
        ),
    ),
);
